import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { formatDate } from '@/lib/format';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import type { ApiClientError } from '@/lib/api-client';

import { useConfirmDelay, useRecordDelayReason } from './useCloseDelay';
import type { BatchCloseContext } from './types';

const NS = 'corrections';

/** Batch lifecycle states in which a close-delay action no longer applies. */
const LOCKED_STATUSES = new Set(['closed', 'package_generated', 'report_issued']);

/**
 * Read-only close context (case code + deadline) plus the §18 overdue-close
 * delay workflow: record a delay reason (DK003 / `monthly_close.delay_explain`)
 * then confirm it (EF003 / `monthly_close.approve`).
 */
export const CloseDelayPanel = ({ batch }: { batch: BatchCloseContext }) => {
  const { t } = useTranslation(NS);
  const user = useCurrentUser();

  const canExplain = hasPermission(user, 'monthly_close.delay_explain');
  const canApprove = hasPermission(user, 'monthly_close.approve');

  const [reason, setReason] = useState('');

  const record = useRecordDelayReason(batch.monthly_batch_id);
  const confirm = useConfirmDelay(batch.monthly_batch_id);

  const isLocked = LOCKED_STATUSES.has(batch.status);
  const actionError: ApiClientError | null = record.error ?? confirm.error ?? null;
  const anyPending = record.isPending || confirm.isPending;

  const handleRecord = async () => {
    if (reason.trim() === '') return;
    await record.mutateAsync(reason.trim());
    setReason('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('efs.governance.close_delay.title', { defaultValue: '締め遅延' })}</CardTitle>
        <CardDescription>
          {t('efs.governance.close_delay.subtitle', {
            defaultValue: '締め期限を過ぎた場合は、遅延理由の記録と承認が必要です。',
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Read-only context */}
        <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
          <div className="flex justify-between gap-4 border-b border-efs-border py-1.5">
            <dt className="text-sm text-efs-text-muted">
              {t('efs.governance.close_delay.fields.case_code', { defaultValue: 'ケースコード' })}
            </dt>
            <dd className="font-mono text-sm text-efs-text-primary">{batch.case_code ?? '-'}</dd>
          </div>
          <div className="flex justify-between gap-4 border-b border-efs-border py-1.5">
            <dt className="text-sm text-efs-text-muted">
              {t('efs.governance.close_delay.fields.close_deadline', {
                defaultValue: '締め期限',
              })}
            </dt>
            <dd className="text-sm text-efs-text-primary">{formatDate(batch.close_deadline)}</dd>
          </div>
        </dl>

        {actionError ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>
              {t('efs.governance.close_delay.error.title', {
                defaultValue: '操作できませんでした',
              })}
            </AlertTitle>
            <AlertDescription>{actionError.message}</AlertDescription>
          </Alert>
        ) : null}

        {isLocked ? (
          <p className="text-sm text-efs-text-muted">
            {t('efs.governance.close_delay.locked', {
              defaultValue: 'このバッチは確定済みのため、遅延操作はできません。',
            })}
          </p>
        ) : (
          <>
            {/* Record delay reason — DK003 */}
            {canExplain ? (
              <div className="space-y-2">
                <Label htmlFor="delay-reason">
                  {t('efs.governance.close_delay.fields.reason', { defaultValue: '遅延理由' })}
                </Label>
                <Textarea
                  id="delay-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('efs.governance.close_delay.reason_placeholder', {
                    defaultValue: '締めが遅延した理由を入力してください',
                  })}
                  rows={3}
                />
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleRecord}
                    disabled={anyPending || reason.trim() === ''}
                  >
                    {record.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                    {t('efs.governance.close_delay.actions.record', {
                      defaultValue: '遅延理由を記録',
                    })}
                  </Button>
                </div>
              </div>
            ) : null}

            {/* Confirm delay — EF003 */}
            {canApprove ? (
              <div className="flex items-center justify-between gap-4 border-t border-efs-border pt-4">
                <p className="text-sm text-efs-text-muted">
                  {t('efs.governance.close_delay.confirm_hint', {
                    defaultValue: '記録済みの遅延理由を確認し、締め遅延を承認します。',
                  })}
                </p>
                <Button type="button" onClick={() => confirm.mutate()} disabled={anyPending}>
                  {confirm.isPending ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="mr-1 h-4 w-4" />
                  )}
                  {t('efs.governance.close_delay.actions.confirm', {
                    defaultValue: '遅延を承認',
                  })}
                </Button>
              </div>
            ) : null}

            {!canExplain && !canApprove ? (
              <p className="text-sm text-efs-text-muted">
                {t('efs.governance.close_delay.no_access', {
                  defaultValue: '締め遅延を操作する権限がありません。',
                })}
              </p>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CloseDelayPanel;
